import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { TrendingUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u?.plan ? "/" : "/choose-plan");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#090D16] text-slate-100">
      <div className="hidden lg:flex flex-col justify-between p-12 grid-bg border-r border-slate-800 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center glow-primary">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl">Trading Academy</span>
        </div>
        <div className="max-w-md">
          <h1 className="font-display text-5xl font-black leading-tight mb-4">Learn Trading<br />From Zero.</h1>
          <p className="text-slate-400 text-lg">Master markets, charts, strategy and risk — step by step, with a $100,000 virtual simulator. 100% educational.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-amber-400/80">
          <AlertTriangle className="w-4 h-4" /> Virtual money only. Real trading risks capital loss.
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-extrabold text-lg">Trading Academy</span>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold">Sign in</h2>
            <p className="text-slate-400 text-sm mt-1">Continue your learning journey.</p>
          </div>
          {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="you@example.com" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Password</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500 focus:outline-none" placeholder="••••••••" />
          </div>
          <button data-testid="login-submit" disabled={loading} type="submit"
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold rounded-lg py-2.5 transition-colors">
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-sm text-slate-400 text-center">No account? <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="go-register">Create one</Link></p>
        </form>
      </div>
    </div>
  );
}
